import { forwardRef } from "react";
import type {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  ReactNode,
  ElementType,
  MouseEvent,
} from "react";
import { cn } from "@/lib/utils";
import { track, type TrackEventName } from "@/lib/tracking";
import { IconWhatsApp } from "./icons";

export type CTAVariant =
  | "whatsapp"
  | "navy"
  | "navy-outline"
  | "gold"
  | "red"
  | "agro"
  | "light-outline"
  | "ghost-dark"
  | "ghost-light";

export type CTASize = "sm" | "md" | "lg" | "xl";

type CommonProps = {
  variant?: CTAVariant;
  size?: CTASize;
  fullWidth?: boolean;
  fullWidthOnMobile?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  withWhatsAppIcon?: boolean;
  children: ReactNode;
  className?: string;
  /**
   * Rastreamento automático de clique — TODO CTAButton dispara um evento por
   * construção. `trackLabel` identifica o botão (default = texto do children).
   * `trackName` sobrescreve o evento (default "cta_click"). `noTrack` desliga.
   */
  trackLabel?: string;
  trackName?: TrackEventName;
  trackParams?: Record<string, unknown>;
  noTrack?: boolean;
};

type CTAButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
    as?: "button";
    href?: never;
  };

type CTAButtonAsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & {
    as: "a";
    href: string;
  };

export type CTAButtonProps = CTAButtonAsButton | CTAButtonAsAnchor;

const variantClass: Record<CTAVariant, string> = {
  whatsapp:
    "bg-[var(--sr-green-cta)] text-white hover:brightness-110 shadow-[var(--shadow-cta)]",
  navy: "bg-[var(--sr-navy)] text-white hover:bg-[var(--sr-navy-deep)] shadow-[var(--shadow-cta-navy)]",
  "navy-outline":
    "border border-[var(--sr-navy)]/30 text-[var(--sr-navy)] bg-white hover:bg-[var(--sr-navy)]/5",
  gold: "bg-[var(--sr-gold)] text-[var(--sr-navy-deep)] hover:bg-[var(--sr-gold-light)] shadow-[var(--shadow-cta-gold)]",
  red: "bg-[var(--sr-red)] text-white hover:bg-[var(--sr-red-dark)] shadow-[var(--shadow-cta-red)]",
  agro: "bg-[var(--sr-agro)] text-white hover:bg-[var(--sr-agro-dark)] shadow-[0_8px_30px_rgba(46,125,50,0.25)]",
  "light-outline":
    "border border-white/30 text-white bg-transparent hover:bg-white/10",
  "ghost-dark": "text-[var(--sr-navy)] hover:bg-[var(--sr-navy)]/5",
  "ghost-light": "text-white/85 hover:text-white hover:bg-white/10",
};

const sizeClass: Record<CTASize, string> = {
  sm: "text-xs px-4 py-2.5 gap-1.5 rounded-[var(--radius-sm-token)]",
  md: "text-sm px-5 py-3 gap-2 rounded-[var(--radius-md-token)]",
  lg: "text-sm px-6 py-3.5 gap-2 rounded-[var(--radius-md-token)]",
  xl: "text-sm sm:text-base px-8 py-4 gap-2.5 rounded-[var(--radius-md-token)]",
};

const baseClass =
  "relative inline-flex items-center justify-center font-semibold whitespace-nowrap " +
  "transition-all duration-200 sr-cta-magnetic sr-focus-ring sr-shine sr-ui " +
  "disabled:opacity-50 disabled:pointer-events-none";

/**
 * CTAButton, botão CTA unificado do site SR.
 *
 * Props:
 * - variant: cor / estilo
 * - size: sm | md | lg | xl
 * - fullWidth: ocupa 100% sempre; fullWidthOnMobile: 100% só mobile
 * - withWhatsAppIcon: ícone WhatsApp à esquerda automático
 * - as="a" + href: vira <a>; default: <button>
 */
export const CTAButton = forwardRef<HTMLElement, CTAButtonProps>(
  (props, ref) => {
    const {
      variant = "navy",
      size = "lg",
      fullWidth,
      fullWidthOnMobile,
      iconLeft,
      iconRight,
      withWhatsAppIcon,
      children,
      className,
      trackLabel,
      trackName,
      trackParams,
      noTrack,
      ...rest
    } = props;

    const fireTrack = () => {
      if (noTrack) return;
      track(trackName ?? "cta_click", {
        label:
          trackLabel ?? (typeof children === "string" ? children : undefined),
        variant,
        ...trackParams,
      });
    };

    const classes = cn(
      baseClass,
      variantClass[variant],
      sizeClass[size],
      fullWidth && "w-full",
      fullWidthOnMobile && "w-full sm:w-auto",
      className
    );

    const iconSizeClass =
      size === "sm" ? "w-3.5 h-3.5" : size === "xl" ? "w-5 h-5" : "w-4 h-4";

    const content = (
      <span className="relative z-[2] inline-flex items-center gap-[inherit]">
        {withWhatsAppIcon && <IconWhatsApp className={iconSizeClass} />}
        {iconLeft && (
          <span className={cn("inline-flex", iconSizeClass)}>{iconLeft}</span>
        )}
        <span className="inline-flex items-center">{children}</span>
        {iconRight && (
          <span className={cn("inline-flex", iconSizeClass)}>{iconRight}</span>
        )}
      </span>
    );

    if (props.as === "a") {
      const {
        as: _as,
        href,
        onClick,
        ...anchorRest
      } = rest as AnchorHTMLAttributes<HTMLAnchorElement> & {
        as?: ElementType;
      };
      const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        fireTrack();
        onClick?.(e);
      };
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          onClick={handleClick}
          {...anchorRest}
        >
          {content}
        </a>
      );
    }

    const {
      as: _as,
      onClick,
      ...buttonRest
    } = rest as ButtonHTMLAttributes<HTMLButtonElement> & {
      as?: ElementType;
    };
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      fireTrack();
      onClick?.(e);
    };
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={
          (buttonRest as ButtonHTMLAttributes<HTMLButtonElement>).type ??
          "button"
        }
        className={classes}
        onClick={handleClick}
        {...buttonRest}
      >
        {content}
      </button>
    );
  }
);

CTAButton.displayName = "CTAButton";
